'use client';

import { CaretDown } from 'phosphor-icons';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { c } from 'ui-components';
import { FacetDropdownProps } from './FacetDropdown.types';

export const FacetDropdown = ({
  label,
  selectedCount,
  children,
  clearLabel,
  onClear,
  className,
}: FacetDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  const displayLabel =
    selectedCount > 0 ? `${label} (${selectedCount})` : label;

  return (
    <div ref={containerRef} className={c('relative', className)}>
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className={c(
          'group flex w-full items-center justify-between gap-x-2 rounded-lg border border-grey-400 py-2 pl-3 pr-2 text-sm font-normal text-[#141414]',
          'focus-outline',
          isOpen ? 'bg-white' : 'bg-transparent'
        )}
      >
        <span
          className={c(
            'truncate',
            isOpen ? 'opacity-100' : 'opacity-90 group-hover:opacity-100'
          )}
        >
          {displayLabel}
        </span>
        <CaretDown
          size={15}
          className={c('shrink-0 transition-transform', {
            'rotate-180': isOpen,
          })}
        />
      </button>

      {isOpen && (
        <div
          id={panelId}
          role="group"
          aria-label={label}
          className="absolute left-0 top-full z-50 mt-1 min-w-full whitespace-nowrap rounded-lg border border-grey-200 bg-white p-2 shadow-lg"
        >
          {children}
          {selectedCount > 0 && onClear && (
            <div className="px-1.5">
              <button
                type="button"
                onClick={onClear}
                className="p-1.5 text-[13px] font-medium leading-5 text-blue"
              >
                {clearLabel ?? 'Clear'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
